import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "layouts/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { ROUTES } from "config/navConfig";
import NavigationProgressTracker from "components/ui/NavigationProgressTracker";
import StepSPVBasics from "./components/StepSPVBasics";
import StepCapTable from "./components/StepCapTable";
import StepFees from "./components/StepFees";
import StepTimeline from "./components/StepTimeline";
import StepTokenisation from "./components/StepTokenisation";
import SummaryPanel from "./components/SummaryPanel";
import ConfirmationScreen from "./components/ConfirmationScreen";

const STEPS = [
  { label: "SPV Basics", description: "Name, jurisdiction, vehicle type", icon: "Building2" },
  { label: "Cap Table", description: "Investors & allocations", icon: "Users" },
  { label: "Fees", description: "Management fee & carry", icon: "Percent" },
  { label: "Timeline", description: "Closing dates & milestones", icon: "Calendar" },
  { label: "Tokenisation", description: "RWA readiness", icon: "Coins" },
];

const INITIAL_DATA = {
  // Basics
  name: "Tyne Bridge Refinancing SPV",
  referenceCode: "GIF2-2026-001",
  jurisdiction: "england-wales",
  sector: "transport",
  vehicleType: "limited-partnership",
  targetSize: "120000000",
  minTicket: "10000000",
  targetIRR: "10.8",
  description: "Transport infrastructure refinancing SPV. Tyne Bridge toll road and supporting transport corridor assets in the North East of England. Contracted revenue stream with 22-year DBFM agreement. Fund II vehicle targeting £85m equity alongside £180m senior debt facility.",
  // Cap Table
  investors: [
    { id: "inv-1", name: "Nordic Pension Fund", type: "pension-fund", commitment: "40000000", allocation: "33", status: "soft-circle", isAnchor: true },
    { id: "inv-2", name: "Abu Dhabi Investment Authority", type: "sovereign-wealth", commitment: "35000000", allocation: "29", status: "soft-circle", isAnchor: true },
  ],
  gpCommitment: "10000000",
  gpCommitmentPct: "8",
  // Fees
  managementFee: "1.5",
  feeBasis: "committed",
  feePeriod: "investment-period",
  carry: "20",
  hurdleRate: "8",
  carryStructure: "whole-fund",
  catchUp: "full",
  platformFee: "0.5",
  transactionFee: "75000",
  adminFee: "30000",
  // Timeline
  marketingDate: "2026-03-01",
  firstCloseDate: "2026-07-31",
  finalCloseDate: "2026-10-31",
  investmentPeriodEnd: "2030-10-31",
  fundTerm: "10",
  extensionOptions: "2",
  callFrequency: "as-needed",
  callNoticePeriod: "10",
  maxSingleCall: "25",
  milestones: [
    { id: "m1", label: "IC Approval", date: "2026-04-13", description: "Investment Committee approval — 4-member quorum required", status: "pending", critical: true },
    { id: "m2", label: "LPA Execution", date: "2026-05-31", description: "Limited Partnership Agreement signed by all anchor LPs", status: "pending", critical: true },
    { id: "m3", label: "First Close", date: "2026-07-31", description: "Minimum £85m committed — fund formation complete", status: "pending", critical: true },
    { id: "m4", label: "Debt Facility Signed", date: "2026-08-15", description: "£180m senior debt facility with NatWest Infrastructure Finance", status: "pending", critical: false },
    { id: "m5", label: "Asset Acquisition", date: "2026-09-30", description: "Completion of toll road refinancing transaction", status: "pending", critical: true },
  ],
  // Tokenisation
  tokenisationEnabled: false,
  tokenStandard: "",
  blockchainNetwork: "",
  tokenSupply: "",
  transferRestrictions: [],
  secondaryTradingEnabled: false,
  secondaryVenue: "",
  lockUpPeriod: "",
  amlScreeningEnabled: false,
  dividendAutomation: false,
};

function validateStep(step, data) {
  const errors = {};
  if (step === 0) {
    if (!data?.name?.trim()) errors.name = "SPV name is required.";
    if (!data?.jurisdiction) errors.jurisdiction = "Please select a jurisdiction.";
    if (!data?.vehicleType) errors.vehicleType = "Please select a vehicle type.";
    if (!data?.sector) errors.sector = "Please select a primary sector.";
    if (!data?.targetSize || parseFloat(data?.targetSize) <= 0) errors.targetSize = "Please enter a valid target fund size.";
  }
  if (step === 2) {
    if (!data?.managementFee) errors.managementFee = "Management fee is required.";
    if (!data?.carry) errors.carry = "Carried interest is required.";
  }
  if (step === 3) {
    if (!data?.firstCloseDate) errors.firstCloseDate = "First close date is required.";
    if (!data?.finalCloseDate) errors.finalCloseDate = "Final close date is required.";
  }
  return errors;
}

export default function SPVCreationWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_DATA);
  const [errors, setErrors] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [userRole] = useState("investor");

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleNext = () => {
    const stepErrors = validateStep(currentStep, formData);
    if (Object.keys(stepErrors)?.length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    if (currentStep < STEPS?.length - 1) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      handleSubmit();
    }
  };

  const handlePrev = () => {
    setErrors({});
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleStepClick = (index) => {
    if (index <= currentStep) {
      setErrors({});
      setCurrentStep(index);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    await new Promise((r) => setTimeout(r, 800));
    setIsSaving(false);
    showToast("Draft saved successfully. You can return to complete this SPV at any time.", "success");
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    await new Promise((r) => setTimeout(r, 1200));
    setIsSaving(false);
    setIsCompleted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderStep = () => {
    const props = { data: formData, onChange: setFormData, errors };
    switch (currentStep) {
      case 0: return <StepSPVBasics {...props} />;
      case 1: return <StepCapTable {...props} />;
      case 2: return <StepFees {...props} />;
      case 3: return <StepTimeline {...props} />;
      case 4: return <StepTokenisation {...props} />;
      default: return null;
    }
  };

  return (
    <AppLayout userRole={userRole}>
      <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
        {/* Toast */}
        {toast && (
          <div
            className="fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg max-w-sm"
            style={{
              background: toast?.type === "success" ? "var(--color-success)" : "var(--color-error)",
              color: "white",
              zIndex: "var(--z-toast)",
            }}
          >
            <Icon name={toast?.type === "success" ? "CheckCircle2" : "AlertCircle"} size={18} color="white" />
            <p className="font-caption text-sm">{toast?.message}</p>
            <button onClick={() => setToast(null)} className="ml-auto flex-shrink-0">
              <Icon name="X" size={16} color="white" />
            </button>
          </div>
        )}

        {/* Page Header */}
        <div
          className="border-b px-4 md:px-6 lg:px-8 py-4 lg:py-5"
          style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}
        >
          <div className="max-w-screen-xl mx-auto">
            <div className="flex items-center gap-3 mb-1">
              <button
                onClick={() => navigate(ROUTES.opportunities)}
                className="flex items-center gap-1.5 font-caption text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                <Icon name="ChevronLeft" size={16} />
                <span className="hidden sm:inline">Opportunities</span>
              </button>
              <Icon name="ChevronRight" size={14} color="var(--color-muted-foreground)" />
              <span className="font-caption text-sm text-foreground font-medium">SPV Creation Wizard</span>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="font-heading text-xl lg:text-2xl font-semibold text-foreground">
                  Create Special Purpose Vehicle
                </h1>
                <p className="font-caption text-sm text-muted-foreground mt-0.5">
                  Structure your infrastructure equity investment vehicle in 5 steps.
                </p>
              </div>
              {!isCompleted && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Save"
                  iconPosition="left"
                  iconSize={15}
                  loading={isSaving}
                  onClick={handleSaveDraft}
                  className="flex-shrink-0"
                >
                  <span className="hidden sm:inline">Save Draft</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        {isCompleted ? (
          <div className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-8">
            <ConfirmationScreen data={formData} />
          </div>
        ) : (
          <div className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-6 lg:py-8">
            {/* Progress Tracker */}
            <div className="mb-6 lg:mb-8 px-2">
              <NavigationProgressTracker
                steps={STEPS}
                currentStep={currentStep}
                onStepClick={handleStepClick}
                orientation="horizontal"
              />
            </div>

            {/* Main Content */}
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              {/* Form Area */}
              <div className="flex-1 min-w-0">
                <div
                  className="rounded-xl border p-4 md:p-6 lg:p-8"
                  style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}
                >
                  {/* Step Header */}
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b" style={{ borderColor: "var(--color-border)" }}>
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: "rgba(59,76,76,0.1)" }}
                    >
                      <Icon name={STEPS?.[currentStep]?.icon} size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-caption text-xs text-muted-foreground">
                          Step {currentStep + 1} of {STEPS?.length}
                        </span>
                      </div>
                      <h2 className="font-heading text-lg font-semibold text-foreground">
                        {STEPS?.[currentStep]?.label}
                      </h2>
                    </div>
                  </div>

                  {/* Step Content */}
                  {renderStep()}

                  {/* Navigation Buttons */}
                  <div
                    className="flex items-center justify-between gap-4 mt-8 pt-6 border-t"
                    style={{ borderColor: "var(--color-border)" }}
                  >
                    <Button
                      variant="outline"
                      iconName="ChevronLeft"
                      iconPosition="left"
                      iconSize={16}
                      onClick={handlePrev}
                      disabled={currentStep === 0}
                    >
                      Previous
                    </Button>

                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Save"
                        iconPosition="left"
                        iconSize={15}
                        loading={isSaving}
                        onClick={handleSaveDraft}
                        className="hidden sm:flex"
                      >
                        Save Draft
                      </Button>
                      <Button
                        variant="default"
                        iconName={currentStep === STEPS?.length - 1 ? "CheckCircle2" : "ChevronRight"}
                        iconPosition="right"
                        iconSize={16}
                        onClick={handleNext}
                        loading={isSaving && currentStep === STEPS?.length - 1}
                      >
                        {currentStep === STEPS?.length - 1 ? "Create SPV" : "Continue"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Panel - Desktop sidebar */}
              <div className="hidden lg:block w-72 xl:w-80 flex-shrink-0">
                <div className="sticky top-6">
                  <SummaryPanel data={formData} currentStep={currentStep} />

                  {/* Quick Navigation */}
                  <div
                    className="mt-4 rounded-xl border p-4"
                    style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}
                  >
                    <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3"
                      style={{ letterSpacing: "0.06em" }}>
                      Quick Navigation
                    </p>
                    <div className="space-y-1">
                      {STEPS?.map((step, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleStepClick(idx)}
                          disabled={idx > currentStep}
                          className="flex items-center gap-2.5 w-full px-3 py-2 rounded-md text-left transition-all duration-200"
                          style={{
                            background: idx === currentStep ? "rgba(59,76,76,0.08)" : "transparent",
                            opacity: idx > currentStep ? 0.4 : 1,
                            cursor: idx > currentStep ? "not-allowed" : "pointer",
                          }}
                        >
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{
                              background: idx < currentStep
                                ? "var(--color-success)"
                                : idx === currentStep
                                ? "var(--color-primary)"
                                : "var(--color-muted)",
                            }}
                          >
                            {idx < currentStep ? (
                              <Icon name="Check" size={10} color="white" strokeWidth={3} />
                            ) : (
                              <span className="font-data text-xs font-semibold"
                                style={{ color: idx === currentStep ? "white" : "var(--color-muted-foreground)", fontSize: "0.625rem" }}>
                                {idx + 1}
                              </span>
                            )}
                          </div>
                          <span
                            className="font-caption text-sm"
                            style={{
                              color: idx === currentStep ? "var(--color-primary)" : "var(--color-foreground)",
                              fontWeight: idx === currentStep ? 600 : 400,
                            }}
                          >
                            {step?.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Help */}
                  <div
                    className="mt-4 p-4 rounded-xl border"
                    style={{ background: "rgba(59,76,76,0.03)", borderColor: "rgba(59,76,76,0.15)" }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="HelpCircle" size={15} color="var(--color-primary)" />
                      <span className="font-caption text-xs font-semibold text-foreground">Need Help?</span>
                    </div>
                    <p className="font-caption text-xs text-muted-foreground leading-relaxed">
                      Contact your SeedDrive relationship manager or visit the Help Centre for guidance on SPV structuring and regulatory requirements.
                    </p>
                    <button className="mt-2 font-caption text-xs font-medium underline" style={{ color: "var(--color-primary)" }}>
                      Open Help Centre
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Summary (collapsible) */}
            <div className="lg:hidden mt-6">
              <details className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--color-border)" }}>
                <summary
                  className="flex items-center justify-between px-4 py-3 cursor-pointer font-caption font-semibold text-sm text-foreground"
                  style={{ background: "var(--color-muted)" }}
                >
                  <div className="flex items-center gap-2">
                    <Icon name="FileText" size={15} color="var(--color-primary)" />
                    SPV Summary
                  </div>
                  <Icon name="ChevronDown" size={16} color="var(--color-muted-foreground)" />
                </summary>
                <div className="p-4" style={{ background: "var(--color-card)" }}>
                  <SummaryPanel data={formData} currentStep={currentStep} />
                </div>
              </details>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}